/**
 * @module lib/store
 */

import logger from "utils/logger";
import isNil from "lodash/isNil";
import { getUrlVars } from "utils/urls";

const debugTag = "[STORE]";
const debugStorage = !isNil(getUrlVars().storage);

/**
 * @method isLocalStorageTypeSupported
 * @description whether or not localStorage is supported/settable in current browser
 * @param {String} storageType - "localStorage" or "sessionStorage"
 * @returns {Boolean}
 */
export function isLocalStorageTypeSupported(storageType) {
  const testKey = "test";
  const localStorageExists = storageType in window;

  if (!localStorageExists) {
    debugStorage && logger.info(`${debugTag} ${storageType} not defined`);
    return false;
  }

  try {
    if (storageType === "localStorage") {
      window.localStorage.setItem(testKey, "1");
      window.localStorage.removeItem(testKey);
    } else if (storageType === "sessionStorage") {
      window.sessionStorage.setItem(testKey, "1");
      window.sessionStorage.removeItem(testKey);
    }

    debugStorage && logger.info(`${debugTag} ${storageType} is supported`);
    return true;
  } catch (error) {
    // storageType does not exist or is full. Default to alternative.
    debugStorage && logger.info(`${debugTag} ${storageType} is unsupported`);
    return false;
  }
}

/**
 * @method getLocalStorageAlternative
 * @description returns an object mocking local storage methods
 * @returns {Object}
 */
function getLocalStorageAlternative() {
  let structureLocalStorage = {};

  return {
    setItem(key, value) {
      structureLocalStorage[key] = value;
    },

    getItem(key) {
      if (structureLocalStorage[key]) {
        return structureLocalStorage[key];
      } else {
        return null;
      }
    },

    removeItem(key) {
      structureLocalStorage[key] = undefined;
    },

    clear() {
      structureLocalStorage = {};
    }
  };
}

/**
 * @method storageMechanism
 * @description checks for different type of storage and consolidates into one interface for manipulating properties
 * @param {String} storageType - window.localStorage or window.sessionStorage
 * @returns {Object}
 */
function storageMechanism(storageType) {
  // check to see if storageType is supported in current browser
  // if not then use local storage alternative
  const isSupported = isLocalStorageTypeSupported(storageType);

  let currentStorage = null;

  if (isSupported) {
    currentStorage = (storageType == "localStorage") ? window.localStorage : window.sessionStorage;
  }

  const storage = isSupported ? currentStorage : getLocalStorageAlternative();

  const store = {
    disabled: false,

    /**
     * Save the key value pair to storage, with optional serialization.
     * @param {String} key - name of the key you want to create/update.
     * @param {*} val - value you want to give the key you are creating/updating.
     * @param {Boolean} [serialize=true] - true if you want to `JSON.stringify()` the value
     * @returns {*}
     */
    set(key, val, serialize = true) {
      const trueValue = val && serialize ? this.serialize(val) : val;
      storage.setItem(key, trueValue);
      return val;
    },

    /**
     * Retrieve the key value pair to storage, with optional deserialization.
     * @param {*} key - name of the key you want to read/get.
     * @param {Boolean} [deserialize=true] - true if you want to `JSON.parse()` the value
     * @returns {*}
     */
    get(key, deserialize = true) {
      let val = storage.getItem(key);

      // because data is serialized we need this if else to return the correct value
      if (typeof val === "string" && val === "undefined") {
        return undefined;
      } else if (typeof val === "string" && val === "null") {
        return null;
      } else if (typeof val === "string" && val === "NaN") {
        return NaN;
      } else if (typeof val === "string" && val === "") {
        return "";
      } else {
        const val = storage.getItem(key);
        return deserialize ? this.deserialize(val) : val;
      }
    },

    has(key) {
      // Even though "false" and "0" are falsy values, they are still values that can exist and be stored in local storage
      return ![undefined, null, NaN, ""].includes(this.get(key));
    },

    remove(key) {
      storage.removeItem(key);
    },

    rename(oldKey, newKey) {
      const oldValue = this.get(oldKey);
      const newValue = this.get(newKey);

      if (oldValue && !newValue) {
        this.set(newKey, oldValue);
      }
      this.remove(oldKey);
    },

    clear() {
      storage.clear();
    },

    serialize(value) {
      return JSON.stringify(value);
    },

    deserialize(value) {
      // TODO: This is incorrect, if `value` is null, it should return null
      if (typeof value !== "string") {
        return undefined;
      }
      try {
        return JSON.parse(value);
      } catch (e) {
        return value || undefined;
      }
    }
  };

  store.enabled = isSupported;
  return store;

}

/**
 * @description an object containing getters and setters for local storage
 * @type {Object}
 */
export const local = storageMechanism("localStorage");

/**
 * @description an object containing getters and setters for local storage
 * @type {Object}
 */
export const session = storageMechanism("sessionStorage");

/**
 * @method __setup__
 * @description setups up both local storage and session storage for testing purposes
 * @returns {Object}
 */
export function __setup__() {
  return {
    local: storageMechanism("localStorage"),
    session: storageMechanism("sessionStorage")
  };
}
